import { HTTPRequestResponse } from '@bimser/common';
import { BCDataGrid } from '@bimser/components';
import { compareMatchWithCollection, IDictionary } from '@bimser/core';
import get from 'lodash/get';
import DataGrid2 from '../..';
import { IDataSourceResponse } from '../../../../../../entities/common';
import { IGetDataSourceData } from '../../../../../../entities/common/DataSource/IGetDataSourceData';
import { getDataSourceData } from '../../../../../../helper';
import { IDataLoadEventArgs } from '../../../../entities/EventArgs';

export const createCustomStore = (dataGrid: DataGrid2) =>
	new BCDataGrid.Components.CustomStore({
		key: dataGrid.getUniqueDataField(),
		load: async _loadOptions => {
			if (dataGrid.__form.displayMode !== 'execution') {
				return {
					data: [],
					totalCount: 0
				};
			}

			const loadOptions = BCDataGrid.Helpers.convertLoadOptions(
				_loadOptions,
				dataGrid.__bcDataGridRef.current
					.instance()
					.option('columns') as BCDataGrid.Entities.IRemoteColumnProps[]
			);

			let rows = [];
			let count = 0;

			try {
				const dataSourceRequestArgs = {
					dataSource: dataGrid.dataSource,
					projectName: dataGrid.__form.projectName,
					reload: true,
					forceRefresh: dataGrid.__onLoadArgs.forceRefresh,
					clientCacheId: dataGrid.__form.__windowId,
					fieldParams: dataGrid.__onLoadArgs.externalData?.fieldParams,
					projectVersion: dataGrid.__form.packageVersion,
					loadOptions: {
						...loadOptions,
						distinct: true,
						filterNulls: true,
						valueExpr: getValueExpr(dataGrid, _loadOptions)
					}
				};
				const response = await getDataSourceData(dataSourceRequestArgs);

				rows = response.data.result;
				count = response.data.count;

				if ((_loadOptions as any).isLoadingAll && rows.length < count) {
					// Excel Export
					rows = await loadAll(dataSourceRequestArgs, count, rows);
				}
			} catch (error) {
				console.error(error);

				rows = [];
				count = 0;
			}

			dataGrid.emit('event:OnDataLoad', { data: rows } as IDataLoadEventArgs);

			await dataGrid.set(
				'rows',
				rows.map(row => dataGrid.newRow({ data: row }))
			);

			if (dataGrid.hiddenRowColumnName && dataGrid.hiddenRowValueMatchers.values.length) {
				// Filter hidden rows
				rows = filterHiddens(dataGrid, rows);
			}

			if (_loadOptions.group) {
				// headerFilterEnabled is true
				rows = rows.map(row => ({ key: row[_loadOptions.group[0].selector] }));
			}

			return {
				data: rows,
				totalCount: count
			};
		},
		remove: _ => Promise.resolve(),
		insert: _ => Promise.resolve(),
		update: (_, __) => Promise.resolve()
	});

async function loadAll(
	dataSourceRequestArgs: IGetDataSourceData,
	count: number,
	rows: IDictionary<any>[]
) {
	const take = 200;
	const requests = Math.ceil(count / take);
	const promises: Promise<HTTPRequestResponse<IDataSourceResponse>>[] = [];

	for (let i = 1; i < requests; i++) {
		const skip = i * take;

		dataSourceRequestArgs.loadOptions.pagination.skip = skip;
		dataSourceRequestArgs.loadOptions.pagination.take = take;

		promises.push(getDataSourceData(dataSourceRequestArgs));
	}
	const results = await Promise.all(promises);

	return [
		...rows,
		...results.reduce((acc, responseItem) => [...acc, ...responseItem.data.result], [])
	];
}

function filterHiddens(dataGrid: DataGrid2, rows: IDictionary<any>[]) {
	return rows.filter(
		row =>
			!dataGrid.hiddenRowValueMatchers.values.some(compareValue =>
				compareMatchWithCollection({
					value: get(row, dataGrid.hiddenRowColumnName),
					editType: dataGrid.hiddenRowValueMatchers.editType,
					dataType: dataGrid
						.__retrieveDataGridInstance()
						.__getBCColumn(dataGrid.hiddenRowColumnName).dbType.type,
					compareValue
				})
			)
	);
}

function getValueExpr(dataGrid: DataGrid2, dxLoadOptions: BCDataGrid.Entities.LoadOptions) {
	let group = dxLoadOptions.group;

	if (Array.isArray(group)) {
		group = group[0];
	}

	return group ? (group as any).selector : dataGrid.getUniqueDataField();
}
